import { useState, useEffect } from 'react';
import { getReviewsByProductId, getRatingBreakdown } from '../../../mocks/reviews';
import type { Review } from '../../../mocks/reviews';

interface ProductReviewsProps {
  productId: string | number;
  productName: string;
  baseRating: number;
  baseReviewCount: number;
}

interface WriteForm {
  author: string;
  rating: number;
  title: string;
  body: string;
}

const STORAGE_KEY = 'user_reviews';

function StarRow({ rating, interactive = false, onSelect }: {
  rating: number;
  interactive?: boolean;
  onSelect?: (r: number) => void;
}) {
  const [hovered, setHovered] = useState(0);
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => {
        const filled = interactive ? (hovered || rating) >= star : rating >= star;
        return (
          <button
            key={star}
            type={interactive ? 'button' : undefined}
            onClick={interactive && onSelect ? () => onSelect(star) : undefined}
            onMouseEnter={interactive ? () => setHovered(star) : undefined}
            onMouseLeave={interactive ? () => setHovered(0) : undefined}
            className={interactive ? 'cursor-pointer' : 'cursor-default'}
            style={{ background: 'none', border: 'none', padding: 0 }}
            aria-label={interactive ? `별점 ${star}점` : undefined}
          >
            <i
              className={`${filled ? 'ri-star-fill text-yellow-400' : 'ri-star-line text-gray-300'} ${interactive ? 'text-2xl' : 'text-sm'} w-5 h-5 flex items-center justify-center`}
            />
          </button>
        );
      })}
    </div>
  );
}

export default function ProductReviews({
  productId,
  productName,
  baseRating,
  baseReviewCount,
}: ProductReviewsProps) {
  const [allReviews, setAllReviews] = useState<Review[]>([]);
  const [sortBy, setSortBy] = useState<'recent' | 'helpful' | 'high' | 'low'>('recent');
  const [showWriteForm, setShowWriteForm] = useState(false);
  const [form, setForm] = useState<WriteForm>({ author: '', rating: 5, title: '', body: '' });
  const [submitted, setSubmitted] = useState(false);
  const [helpfulClicked, setHelpfulClicked] = useState<Set<string>>(new Set());

  useEffect(() => {
    const base = getReviewsByProductId(productId);
    const stored: Review[] = (() => {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw) as Review[];
        return parsed.filter((r) => String(r.productId) === String(productId));
      } catch {
        return [];
      }
    })();
    setAllReviews([...stored, ...base]);
  }, [productId, submitted]);

  const saveUserReview = (review: Review) => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const existing: Review[] = raw ? (JSON.parse(raw) as Review[]) : [];
      existing.unshift(review);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(existing));
    } catch {
      /* ignore */
    }
  };

  const sortedReviews = [...allReviews].sort((a, b) => {
    if (sortBy === 'recent') return new Date(b.date).getTime() - new Date(a.date).getTime();
    if (sortBy === 'helpful') return b.helpful - a.helpful;
    if (sortBy === 'high') return b.rating - a.rating;
    if (sortBy === 'low') return a.rating - b.rating;
    return 0;
  });

  const breakdown = getRatingBreakdown(allReviews);
  const totalCount = allReviews.length;
  const avgRating = totalCount > 0
    ? allReviews.reduce((s, r) => s + r.rating, 0) / totalCount
    : baseRating;
  const displayRating = Number(avgRating.toFixed(1));
  const displayCount = baseReviewCount + (totalCount - getReviewsByProductId(productId).length);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.author.trim() || !form.title.trim() || !form.body.trim()) return;
    const now = new Date();
    const newReview: Review = {
      id: `user-${Date.now()}`,
      productId,
      author: form.author.trim(),
      avatar: form.author.trim()[0] ?? '익',
      rating: form.rating,
      date: now.toISOString().split('T')[0],
      title: form.title.trim(),
      body: form.body.trim(),
      verified: false,
      helpful: 0,
    };
    saveUserReview(newReview);
    setForm({ author: '', rating: 5, title: '', body: '' });
    setShowWriteForm(false);
    setSubmitted((p) => !p);
  };

  const handleHelpful = (id: string) => {
    if (helpfulClicked.has(id)) return;
    setHelpfulClicked((prev) => new Set([...prev, id]));
    setAllReviews((prev) =>
      prev.map((r) => (r.id === id ? { ...r, helpful: r.helpful + 1 } : r))
    );
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
  };

  return (
    <div className="px-4 md:px-8 pb-6">
      {/* 별점 요약 */}
      <div className="flex flex-col sm:flex-row gap-6 sm:gap-10 items-center sm:items-start py-6 border-b border-gray-100">
        {/* 평균 별점 */}
        <div className="flex flex-col items-center flex-shrink-0">
          <p className="text-6xl font-black text-gray-900 leading-none mb-2">{displayRating}</p>
          <StarRow rating={Math.round(displayRating)} />
          <p className="text-xs text-gray-500 mt-1.5">{displayCount.toLocaleString()}개 리뷰</p>
        </div>

        {/* 별점 바 */}
        <div className="flex-1 w-full space-y-2">
          {[5, 4, 3, 2, 1].map((star) => {
            const cnt = breakdown[star] ?? 0;
            const pct = totalCount > 0 ? Math.round((cnt / totalCount) * 100) : 0;
            return (
              <div key={star} className="flex items-center gap-3">
                <div className="flex items-center gap-0.5 w-16 flex-shrink-0">
                  {[...Array(star)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-xs w-3 h-3 flex items-center justify-center" />
                  ))}
                </div>
                <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-yellow-400 rounded-full transition-all duration-700"
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <span className="text-xs text-gray-500 w-8 text-right flex-shrink-0">{cnt}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* 리뷰 작성 버튼 */}
      <div className="flex items-center justify-between py-4 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-gray-700">총 {totalCount}개의 리뷰</span>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
            className="ml-2 px-2 py-1 border border-gray-200 rounded-lg text-xs text-gray-600 cursor-pointer focus:outline-none focus:ring-1 focus:ring-emerald-400"
          >
            <option value="recent">최신순</option>
            <option value="helpful">도움순</option>
            <option value="high">별점 높은 순</option>
            <option value="low">별점 낮은 순</option>
          </select>
        </div>
        <button
          type="button"
          onClick={() => setShowWriteForm((p) => !p)}
          className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600 text-white rounded-full text-xs font-bold hover:bg-emerald-700 transition-colors whitespace-nowrap cursor-pointer"
        >
          <i className="ri-pencil-line w-3.5 h-3.5 flex items-center justify-center" />
          리뷰 작성
        </button>
      </div>

      {/* 리뷰 작성 폼 */}
      {showWriteForm && (
        <form
          onSubmit={handleSubmit}
          className="mt-4 mb-4 bg-emerald-50 border border-emerald-200 rounded-2xl p-5"
        >
          <h4 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2">
            <i className="ri-edit-2-line w-4 h-4 flex items-center justify-center text-emerald-600" />
            {productName} 리뷰 작성
          </h4>

          {/* 별점 선택 */}
          <div className="mb-4">
            <label className="block text-xs font-semibold text-gray-700 mb-2">별점 <span className="text-red-500">*</span></label>
            <StarRow rating={form.rating} interactive onSelect={(r) => setForm((p) => ({ ...p, rating: r }))} />
            <p className="text-xs text-gray-500 mt-1">
              {form.rating === 5 ? '매우 만족' : form.rating === 4 ? '만족' : form.rating === 3 ? '보통' : form.rating === 2 ? '불만족' : '매우 불만족'}
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-3 mb-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">닉네임 <span className="text-red-500">*</span></label>
              <input
                type="text"
                value={form.author}
                onChange={(e) => setForm((p) => ({ ...p, author: e.target.value }))}
                placeholder="홍길동"
                maxLength={20}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">리뷰 제목 <span className="text-red-500">*</span></label>
              <input
                type="text"
                value={form.title}
                onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                placeholder="한 줄 요약"
                maxLength={50}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">상세 리뷰 <span className="text-red-500">*</span></label>
            <textarea
              value={form.body}
              onChange={(e) => setForm((p) => ({ ...p, body: e.target.value }))}
              placeholder="제품 사용 경험을 자유롭게 작성해주세요."
              rows={4}
              maxLength={500}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 resize-none"
            />
            <p className="text-xs text-gray-400 text-right">{form.body.length}/500</p>
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setShowWriteForm(false)}
              className="flex-1 py-2.5 border-2 border-gray-200 text-gray-600 rounded-full text-sm font-semibold hover:border-gray-400 transition-colors whitespace-nowrap cursor-pointer"
            >
              취소
            </button>
            <button
              type="submit"
              disabled={!form.author.trim() || !form.title.trim() || !form.body.trim()}
              className="flex-[2] py-2.5 bg-emerald-600 text-white rounded-full text-sm font-bold hover:bg-emerald-700 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
            >
              리뷰 등록하기
            </button>
          </div>
        </form>
      )}

      {/* 리뷰 목록 */}
      <div className="mt-2 space-y-4">
        {sortedReviews.length === 0 && (
          <div className="text-center py-12 text-gray-400">
            <i className="ri-chat-3-line text-4xl mb-3 w-10 h-10 flex items-center justify-center mx-auto" />
            <p className="text-sm">아직 리뷰가 없습니다. 첫 리뷰를 남겨보세요!</p>
          </div>
        )}
        {sortedReviews.map((review) => (
          <div key={review.id} className="py-4 border-b border-gray-100 last:border-0">
            <div className="flex items-start gap-3">
              {/* 아바타 */}
              <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 text-sm font-bold text-emerald-700">
                {review.avatar}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="text-sm font-bold text-gray-900">{review.author}</span>
                  {review.verified && (
                    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-semibold">
                      <i className="ri-shield-check-line w-3 h-3 flex items-center justify-center" />
                      구매 확인
                    </span>
                  )}
                  <span className="text-xs text-gray-400 ml-auto">{formatDate(review.date)}</span>
                </div>

                {/* 별점 */}
                <div className="flex items-center gap-0.5 mb-2">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <i
                      key={s}
                      className={`${s <= review.rating ? 'ri-star-fill text-yellow-400' : 'ri-star-line text-gray-200'} text-xs w-3.5 h-3.5 flex items-center justify-center`}
                    />
                  ))}
                </div>

                {/* 제목 & 본문 */}
                <p className="text-sm font-semibold text-gray-900 mb-1">{review.title}</p>
                <p className="text-sm text-gray-600 leading-relaxed">{review.body}</p>

                {/* 도움이 됐어요 */}
                <button
                  type="button"
                  onClick={() => handleHelpful(review.id)}
                  disabled={helpfulClicked.has(review.id)}
                  className={`mt-2.5 flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border transition-all cursor-pointer whitespace-nowrap ${
                    helpfulClicked.has(review.id)
                      ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
                      : 'border-gray-200 text-gray-500 hover:border-gray-400 hover:text-gray-700'
                  } disabled:cursor-default`}
                >
                  <i className={`${helpfulClicked.has(review.id) ? 'ri-thumb-up-fill' : 'ri-thumb-up-line'} w-3.5 h-3.5 flex items-center justify-center`} />
                  도움이 됐어요 ({review.helpful})
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
